import { FC } from 'react';
import { useHistory } from 'react-router';
import { HStack, Image, Link, Stack } from '@chakra-ui/react';

import rebrandedVirtualCard from 'assets/images/paymentCards/rebranded/virtual@2x.png';
import useActivationOnboardingProgress from 'common/hooks/useActivationOnboardingProgress/useActivationOnboardingProgress';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import useCreditCards from 'features/PaymentCards/hooks/useCreditCards';
import * as Layout from 'layouts/StepperLayout';

import shieldImg from '../../../../../../../../../assets/brand/shield.png';
import tpeImg from '../../../../../../../../../assets/brand/tpe.png';
import virtualCardImg from '../../../../../../../../../assets/brand/virtual-card.png';
import MultipleCardPanel, {
  MultipleCardPanelItem,
} from '../../../../../../../../../components/MultipleCardPanel/index';
import * as routes from '../../../../../../../routes';
import { CtaButton, SkipStepButton } from '../../../../../components/Buttons';
import locales from './locales';

const HELP_ARTICLE_LINK =
  'https://help.shine.fr/fr/articles/5096371-comment-fonctionnent-les-cartes-virtuelles-shine';

const FirstVirtualCardSummary: FC<
  StepProps & { doesPlanIncludeVCAutoOrder: boolean }
> = ({ doesPlanIncludeVCAutoOrder }) => {
  const { replace } = useHistory();

  const virtualCardAdvantages: MultipleCardPanelItem[] = [
    {
      color: 'red.100',
      img: virtualCardImg,
      name: 'secure',
      title: locales.advantages.secure as string,
    },
    {
      color: 'blue.100',
      img: tpeImg,
      name: 'compatible',
      title: locales.advantages.pay as string,
    },
    {
      color: 'orange.100',
      img: shieldImg,
      name: 'swift',
      title: locales.advantages.swift as string,
    },
  ];

  const {
    error: userCardsError,
    loading: userCardsLoading,
    userCards,
  } = useCreditCards({ fetchPolicy: 'cache-only' });

  const { refetch: refetchActivationOnboardingProgress } =
    useActivationOnboardingProgress({ fetchPolicy: 'cache-only' });

  const [virtualCard] = userCards.filter((c) => !c.isPhysical);

  const formattedPan =
    virtualCard &&
    virtualCard.__typename === 'CreditCard' &&
    locales.formatString(locales.maskedPan, {
      panEnd: virtualCard.maskedPan.slice(-4),
      panStart: virtualCard.maskedPan.slice(0, 4),
    });

  const isVirtualCardPending =
    doesPlanIncludeVCAutoOrder && userCards.every((c) => c.isPhysical);

  const getDescription = () => {
    if (!doesPlanIncludeVCAutoOrder) {
      return locales.basicPlan.description;
    }

    if (isVirtualCardPending) {
      return locales.descriptionWithDelay;
    }

    return locales.description;
  };

  const goToCardsPage = () => {
    refetchActivationOnboardingProgress();
    replace(routes.root);
  };

  if (userCardsLoading) {
    return <Loader />;
  }

  if (userCardsError) {
    return <ErrorView />;
  }

  const goToPermanentVirtualCardOrder = () => {
    replace(routes.orderNewVirtual, { fromFirstVirtualCardSummary: true });
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.BodyScrollableSection>
          <Stack marginBottom="space-32" spacing="space-8">
            <Heading size="lg">{locales.title}</Heading>
            <Text size="md">{getDescription()}</Text>
          </Stack>

          {!doesPlanIncludeVCAutoOrder ? null : (
            <Text fontWeight="weight-500" marginBottom="space-12">
              {locales.detailsTitle}
            </Text>
          )}

          {!doesPlanIncludeVCAutoOrder ? null : (
            <SunshineCard.Group marginBottom="space-40">
              <SunshineCard
                alignItems="center"
                title={
                  isVirtualCardPending || !formattedPan
                    ? locales.cardName
                    : formattedPan
                }
              >
                <SunshineCard.Slot name="aside">
                  <Image
                    alt=""
                    role="presentation"
                    src={rebrandedVirtualCard}
                    width={110}
                  />
                </SunshineCard.Slot>

                {isVirtualCardPending ? null : (
                  <SunshineCard.Content>
                    <HStack>
                      <Text>
                        {locales.cvv}

                        <Text as="span" display="inline">
                          {locales.cvvMasked}
                        </Text>
                      </Text>

                      <Text>
                        {locales.expirationDate}

                        <Text as="span" display="inline">
                          {locales.expirationDateMasked}
                        </Text>
                      </Text>
                    </HStack>
                  </SunshineCard.Content>
                )}

                <SunshineCard.Slot name="action">
                  {isVirtualCardPending ? (
                    <SunshineTag colorScheme="blue" size="sm">
                      {locales.statusWithDelay}
                    </SunshineTag>
                  ) : (
                    <SunshineTag colorScheme="green" size="sm">
                      {locales.status.ACTIVE}
                    </SunshineTag>
                  )}
                </SunshineCard.Slot>
              </SunshineCard>
            </SunshineCard.Group>
          )}

          <MultipleCardPanel items={virtualCardAdvantages} />
          <Link
            color="primary"
            fontWeight="weight-500"
            href={HELP_ARTICLE_LINK}
            marginTop="space-24"
            textDecoration="underline"
          >
            {locales.learnMore}
          </Link>
        </Layout.BodyScrollableSection>

        <Layout.FooterButtonsContainer>
          {!doesPlanIncludeVCAutoOrder ? (
            <>
              <SkipStepButton onClick={goToCardsPage}>
                {locales.basicPlan.skip}
              </SkipStepButton>
              <CtaButton onClick={goToPermanentVirtualCardOrder}>
                {locales.basicPlan.buttonText}
              </CtaButton>
            </>
          ) : (
            <CtaButton onClick={goToCardsPage}>{locales.buttonText}</CtaButton>
          )}
        </Layout.FooterButtonsContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default FirstVirtualCardSummary;
